/**
 * Подписи типов событий и формальности Google Calendar (CTHS-171/173/175).
 *
 * Один источник истины для карточки интеграции, блока «Сегодня и скоро» и всех будущих
 * потребителей: тип приходит с бэкенда кодом (`BUSINESS_MEETING`), а человеку нужно слово.
 */

/** Тип события (`CalendarEventType` на бэкенде). */
export const EVENT_TYPE_LABELS: Record<string, string> = {
    WORK: 'Работа',
    BUSINESS_MEETING: 'Деловая встреча',
    PERSONAL: 'Личное',
    SPORT: 'Спорт',
    TRAVEL: 'Поездка',
    RESTAURANT: 'Ресторан',
    HOLIDAY: 'Праздник',
    ONLINE_MEETING: 'Онлайн-встреча',
    FREE_TIME: 'Свободное время',
    UNKNOWN: 'Не определено',
};

/** Требуемая формальность образа (`EventFormality` на бэкенде). */
export const FORMALITY_LABELS: Record<string, string> = {
    CASUAL: 'Повседневный',
    SMART_CASUAL: 'Смарт-кэжуал',
    BUSINESS: 'Деловой',
    FORMAL: 'Торжественный',
};

/**
 * Цвет бейджа формальности. Не «чем формальнее, тем краснее»: красный означал бы ошибку.
 * Шкала нейтральная — от серого повседневного к насыщенному торжественному.
 */
export const FORMALITY_BADGE_CLASSES: Record<string, string> = {
    CASUAL: 'bg-gray-100 text-gray-700 dark:bg-ink-700 dark:text-gray-200',
    SMART_CASUAL: 'bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300',
    BUSINESS: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300',
    FORMAL: 'bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300',
};

export function eventTypeLabel(type: string | null | undefined): string {
    return (type && EVENT_TYPE_LABELS[type]) || EVENT_TYPE_LABELS.UNKNOWN;
}

export function formalityLabel(formality: string | null | undefined): string {
    return (formality && FORMALITY_LABELS[formality]) || FORMALITY_LABELS.CASUAL;
}

export function formalityBadgeClass(formality: string | null | undefined): string {
    return (formality && FORMALITY_BADGE_CLASSES[formality]) || FORMALITY_BADGE_CLASSES.CASUAL;
}

/**
 * Время события одной строкой: «10:00 – 11:30», «Весь день». Дата не показывается — её несёт
 * заголовок группы («Сегодня», «Завтра»), иначе строка читается как таблица.
 */
export function eventTimeLabel(startTime: string, endTime: string | null, allDay: boolean): string {
    if (allDay) {
        return 'Весь день';
    }
    const start = formatTime(startTime);
    const end = endTime ? formatTime(endTime) : null;
    return end ? `${start} – ${end}` : start;
}

function formatTime(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'});
}

/**
 * Заголовок дня для группировки: «Сегодня» / «Завтра» / «пятница, 19 сентября».
 * Относительные подписи там, где они честны, — в остальных случаях дата с днём недели.
 */
export function eventDayLabel(startTime: string, now: Date = new Date()): string {
    const date = new Date(startTime);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const days = Math.round(
        (startOfDay(date).getTime() - startOfDay(now).getTime()) / 86400000,
    );
    if (days === 0) {
        return 'Сегодня';
    }
    if (days === 1) {
        return 'Завтра';
    }
    return date.toLocaleDateString('ru-RU', {weekday: 'long', day: 'numeric', month: 'long'});
}

function startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
